import { BoardCounts, BoardSeverity, BoardState } from '../../api/ach/board';
import posthog from '../../util/posthog';
import { IssueRef } from './issueRef';

// PostHog event taxonomy for the alerts board. Until now the board had no first-class
// events at all — the dashboards leaned on autocapture ("clicks where the text is Ack"),
// which can't see keyboard acks and carries no rule/severity/timing context. Everything
// the dashboards slice by is captured here as flat properties, so no event needs a join:
//
//   alerts_viewed        one per distinct board view (filters + faceted counts); the
//                        5-minute poll and refocus refetches are deduped by the caller.
//   alert_inspected      an issue opened in the inspector, by row click or j/k.
//   alert_acked          one per issue acked, whatever the affordance — the bulk
//                        "Ack all <rule>" chip reports one event per issue it covers,
//                        so counting alert_acked always counts issues. seconds_open
//                        (first_seen -> ack) is the time-to-ack metric.
//   alert_ask_ai_clicked issue- or contract-scope AI triage opened.

export type AckSource = 'row' | 'inspector' | 'keyboard' | 'group';
export type InspectSource = 'row' | 'keyboard';

const issueProps = (item: IssueRef) => ({
  issue_id: item.issue.id,
  rule_id: item.issue.rule_id,
  severity: item.issue.severity,
  state: item.issue.state,
  contract_id: item.contract.contract_id,
  insertion_order_id: item.insertionOrderId,
  campaign_id: item.issue.campaign_id,
});

// Whole seconds the issue had been open when acked; null when first_seen is unparsable.
const secondsOpen = (firstSeen: string): number | null => {
  const t = Date.parse(firstSeen);
  return Number.isFinite(t) ? Math.max(0, Math.round((Date.now() - t) / 1000)) : null;
};

export const captureAlertAcked = (item: IssueRef, source: AckSource): void => {
  posthog.capture('alert_acked', {
    ...issueProps(item),
    source,
    seconds_open: secondsOpen(item.issue.first_seen),
  });
};

export const captureAlertInspected = (item: IssueRef, source: InspectSource): void => {
  posthog.capture('alert_inspected', { ...issueProps(item), source });
};

export const captureAskAiIssue = (item: IssueRef): void => {
  posthog.capture('alert_ask_ai_clicked', { context: 'issue', ...issueProps(item) });
};

export const captureAskAiContract = (contractId: number): void => {
  posthog.capture('alert_ask_ai_clicked', { context: 'contract', contract_id: contractId });
};

export interface AlertsViewState {
  tab: BoardState;
  severity?: BoardSeverity;
  scope: string;
  ruleIds: string[];
  campaignId?: number;
  insertionOrderId?: number;
  page: number;
}

export const captureAlertsViewed = (view: AlertsViewState, counts: BoardCounts): void => {
  posthog.capture('alerts_viewed', {
    tab: view.tab,
    severity_filter: view.severity ?? null,
    scope: view.scope,
    rule_ids: view.ruleIds,
    campaign_id: view.campaignId ?? null,
    insertion_order_id: view.insertionOrderId ?? null,
    page: view.page,
    open_count: counts.by_state.open,
    acknowledged_count: counts.by_state.acknowledged,
    resolved_count: counts.by_state.resolved,
    blocker_count: counts.by_severity.blocker,
    critical_count: counts.by_severity.critical,
    warning_count: counts.by_severity.warning,
    info_count: counts.by_severity.info,
    unacked_high_sev_over_24h: counts.unacked_high_sev_over_24h,
  });
};
